import * as React from 'react';
import { AppstoreOutlined, UnorderedListOutlined, InsertRowAboveOutlined } from '@ant-design/icons';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { HocOptions } from 'components/common/generic-hoc.types';
import GenericHoc from 'components/common/generic-hoc';

// JSS Styles
const viewToggleStyles = (theme: ThemeType) => ({
    viewTogglePill: {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        border: '1px solid #D9D9D9',
        borderRadius: '20px',
        padding: '2px',
        position: 'relative',
        gap: '4px',
        minWidth: '112px', // Accommodate 3 buttons (32px * 3 + 4px gaps + 4px padding)
    },
    viewToggleOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 200ms ease-in-out',
        position: 'relative',
        zIndex: 1,
        outline: 'none',
        '&[aria-checked="true"]': {
            backgroundColor: theme.primaryColor,
            color: '#FFFFFF',
            boxShadow: `0 2px 4px ${theme.primaryColor}40`,
        },
        '&[aria-checked="false"]': {
            backgroundColor: 'transparent',
            color: '#8C8C8C',
        },
        '&:hover[aria-checked="false"]': {
            color: '#595959',
        },
        '&:focus': {
            outline: `2px solid ${theme.primaryColor}`,
            outlineOffset: '2px',
        },
        '& .anticon': {
            fontSize: 14,
        },
    },
});

interface ViewToggleProps extends StylesProps<ReturnType<typeof viewToggleStyles>> {
    viewMode: string | null | undefined; // Current view or null
    onViewModeChange: (mode: string | null) => void;
    viewIdFromConfig?: string | null | undefined; // Original view_id from config (not used in 3-way toggle)
}

const ViewToggleComponent: React.FC<ViewToggleProps> = ({
    viewMode,
    onViewModeChange,
    classes,
}) => {
    const isGridActive = viewMode === 'grid_view_1';
    const isLaneActive = viewMode === 'lane_view_1';
    const isListActive = !viewMode;

    const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action();
        }
    };

    return (
        <div
            className={classes.viewTogglePill}
            role="radiogroup"
            aria-label="View mode toggle"
        >
            <div
                className={classes.viewToggleOption}
                onClick={() => onViewModeChange(null)}
                onKeyDown={(e) => handleKeyDown(e, () => onViewModeChange(null))}
                role="radio"
                aria-checked={isListActive}
                aria-label="List view"
                tabIndex={0}
            >
                <UnorderedListOutlined />
            </div>
            <div
                className={classes.viewToggleOption}
                onClick={() => onViewModeChange('grid_view_1')}
                onKeyDown={(e) => handleKeyDown(e, () => onViewModeChange('grid_view_1'))}
                role="radio"
                aria-checked={isGridActive}
                aria-label="Grid view"
                tabIndex={0}
            >
                <AppstoreOutlined />
            </div>
            <div
                className={classes.viewToggleOption}
                onClick={() => onViewModeChange('lane_view_1')}
                onKeyDown={(e) => handleKeyDown(e, () => onViewModeChange('lane_view_1'))}
                role="radio"
                aria-checked={isLaneActive}
                aria-label="Lane view"
                tabIndex={0}
            >
                <InsertRowAboveOutlined />
            </div>
        </div>
    );
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: viewToggleStyles,
    },
};

const ViewToggle = GenericHoc(hocConfig)(ViewToggleComponent);

export default ViewToggle;

